import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, WA_DEFAULT } from "@/data/site";
import SmartLink from "@/components/SmartLink";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (to) => !to.includes("#") && location.pathname === to;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-16 sm:h-20 items-center justify-between">
          <SmartLink to="/" data-testid="logo-link" className="group leading-none" aria-label="Millionaire Connector home">
            <span className="block font-mono text-[10px] tracking-[0.35em] text-white/60 group-hover:text-white/90 transition-colors">
              MILLIONAIRE
            </span>
            <span className="block font-display text-lg sm:text-xl font-extrabold tracking-tight text-gradient">
              CONNECTOR
            </span>
          </SmartLink>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <SmartLink
                key={link.label}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[13px] font-medium tracking-wide transition-colors hover:text-white ${
                  isActive(link.to) ? "text-white" : "text-white/60"
                }`}
              >
                {link.label}
              </SmartLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-cta"
              className="btn-neon hidden sm:inline-flex !px-5 !py-2.5 !text-xs"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              BOOK ON WHATSAPP
            </a>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-white transition-colors hover:border-white/40"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-6 pb-8 pt-2 flex flex-col gap-1 bg-black/40 backdrop-blur-xl" aria-label="Mobile">
          {NAV_LINKS.map((link, i) => (
            <SmartLink
              key={link.label}
              to={link.to}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between border-b border-white/5 py-3.5 text-base font-medium text-white/75 transition-colors hover:text-white"
              style={{ transitionDelay: `${i * 30}ms` }}
            >
              {link.label}
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-neon-pink to-neon-blue" aria-hidden="true" />
            </SmartLink>
          ))}
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-menu-whatsapp-cta"
            className="btn-neon mt-5 w-full"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            BOOK ON WHATSAPP
          </a>
        </nav>
      </div>
    </header>
  );
}
